import React, { useState } from 'react'
import * as R from 'ramda'
import PropTypes from 'prop-types'
import { Typography, makeStyles } from '@material-ui/core'
import { useIntl, FormattedMessage } from 'react-intl'

import {
  LIKED,
  DID_LIKE,
  REPORTS,
  SEXUAL_CONTENT,
  VIOLENT_CONTENT,
  HATEFUL_CONTENT,
  HAMEFUL_CONTENT,
  SPAM_CONTENT,
} from 'app/constants/messageDetail'
import DisplaySubscriber from './DisplaySubscriber'
import StackedBarChart from '../components/StackedBarChart'

import SubscriberListWithTabs from './SubscriberListWithTabs'
import SubscriberList from './SubscriberList'

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      width: '900px',
      maxHeight: '500px',
      overflowX: 'auto',
      padding: '2.5rem',
    },
  },
})

const defaultProps = {
  likes: { liked: [], didntLike: [] },
  reports: {
    SEXUAL_CONTENT: [],
    VIOLENT_CONTENT: [],
    HATEFUL_CONTENT: [],
    HAMEFUL_CONTENT: [],
    SPAM_CONTENT: [],
  },
}

const UserReaction = (props) => {
  const { data } = props
  const { likes = defaultProps.likes, reports = defaultProps.reports } =
    data || {}
  const { liked, didntLike } = likes

  const classes = useStyles()
  const { formatMessage } = useIntl()

  const [isOpenReaction, setIsOpenReaction] = useState(false)
  const [isOpenReport, setIsOpenReport] = useState(false)

  const stackedLikeInfo = [
    {
      name: LIKED,
      color: '#4FD975',
      lableTooltip: null,
      value: R.length(liked),
    },
    {
      name: DID_LIKE,
      color: '#F56570',
      lableTooltip: (
        <FormattedMessage
          id="metrics.didnt.like.hover.message"
          values={{
            count: R.length(didntLike),
          }}
        />
      ),
      value: R.length(didntLike),
    },
  ]

  const stackedReportInfo = [
    {
      name: `${REPORTS}-${SEXUAL_CONTENT}`,
      color: '#ff8900',
      lableTooltip: (
        <FormattedMessage
          id="notificationTimeline.card.metric.inappropriate.sexualContent.tooltip"
          values={{
            value: R.length(reports[SEXUAL_CONTENT]),
            b: (text) => <span className="font-bold">{text}</span>,
            newline: <br />,
          }}
        />
      ),
      value: R.length(reports[SEXUAL_CONTENT]),
      label: formatMessage({
        id: 'notificationTimeline.card.metric.inappropriate.sexualContent.title',
      }),
      subscribers: reports[SEXUAL_CONTENT],
    },
    {
      name: `${REPORTS}-${VIOLENT_CONTENT}`,
      color: '#ffa700',
      lableTooltip: (
        <FormattedMessage
          id="notificationTimeline.card.metric.inappropriate.violentOrRepulsiveContent.tooltip"
          values={{
            value: R.length(reports[VIOLENT_CONTENT]),
            b: (text) => <span className="font-bold">{text}</span>,
            newline: <br />,
          }}
        />
      ),
      value: R.length(reports[VIOLENT_CONTENT]),
      label: formatMessage({
        id: 'notificationTimeline.card.metric.inappropriate.violentOrRepulsiveContent.title',
      }),
      subscribers: reports[VIOLENT_CONTENT],
    },
    {
      name: `${REPORTS}-${HATEFUL_CONTENT}`,
      color: '#ffbe43',
      lableTooltip: (
        <FormattedMessage
          id="notificationTimeline.card.metric.inappropriate.hatefulOrAbusiveContent.tooltip"
          values={{
            value: R.length(reports[HATEFUL_CONTENT]),
            b: (text) => <span className="font-bold">{text}</span>,
            newline: <br />,
          }}
        />
      ),
      value: R.length(reports[HATEFUL_CONTENT]),
      label: formatMessage({
        id: 'notificationTimeline.card.metric.inappropriate.hatefulOrAbusiveContent.title',
      }),
      subscribers: reports[HATEFUL_CONTENT],
    },
    {
      name: `${REPORTS}-${HAMEFUL_CONTENT}`,
      color: '#fccf79',
      lableTooltip: (
        <FormattedMessage
          id="notificationTimeline.card.metric.inappropriate.harmfulOrDangerousAct.tooltip"
          values={{
            value: R.length(reports[HAMEFUL_CONTENT]),
            b: (text) => <span className="font-bold">{text}</span>,
            newline: <br />,
          }}
        />
      ),
      value: R.length(reports[HAMEFUL_CONTENT]),
      label: formatMessage({
        id: 'notificationTimeline.card.metric.inappropriate.harmfulOrDangerousAct.title',
      }),
      subscribers: reports[HAMEFUL_CONTENT],
    },
    {
      name: `${REPORTS}-${SPAM_CONTENT}`,
      color: '#fde1ab',
      lableTooltip: (
        <FormattedMessage
          id="notificationTimeline.card.metric.inappropriate.spamOrMisleading.tooltip"
          values={{
            value: R.length(reports[SPAM_CONTENT]),
            b: (text) => <span className="font-bold">{text}</span>,
            newline: <br />,
          }}
        />
      ),
      value: R.length(reports[SPAM_CONTENT]),
      label: formatMessage({
        id: 'notificationTimeline.card.metric.inappropriate.spamOrMisleading.title',
      }),
      subscribers: reports[SPAM_CONTENT],
    },
  ]

  const flatSubscriber = R.flatten(
    R.map(R.prop('subscribers'), stackedReportInfo),
  )

  return (
    <>
      <div className="space-y-3 px-5 py-10">
        <Typography className="font-bold flex items-center text-grey-400 font-medium">
          {formatMessage({
            id: 'notificationTimeline.card.metric.userReaction.title',
          })}
        </Typography>
        {R.isEmpty(liked) ? null : (
          <div className="flex mt-10">
            <span
              className="w-3 h-3 mt-2 mr-3 rounded-full"
              style={{ backgroundColor: '#4FD975' }}
            />
            <Typography className="text-12 text-grey-400">
              <FormattedMessage
                id="metrics.user.reaction.success.message"
                values={{
                  count: R.length(liked),
                }}
              />
            </Typography>
          </div>
        )}
        <StackedBarChart data={stackedLikeInfo} />
        <div className="flex justify-between">
          {R.isEmpty(liked) ? null : (
            <DisplaySubscriber
              data={liked}
              onClick={() => setIsOpenReaction(true)}
            />
          )}
        </div>
      </div>

      <SubscriberList
        subscribers={liked}
        title={formatMessage({ id: 'metrics.reaction.title' })}
        onClose={() => setIsOpenReaction(false)}
        isOpen={isOpenReaction}
      />

      <div className="space-y-3 px-5 py-10">
        <Typography className="font-bold flex items-center text-grey-400 font-medium">
          {formatMessage({
            id: 'notificationTimeline.card.metric.userReaction.inappropriateBreakdown.title',
          })}
        </Typography>
        <StackedBarChart data={stackedReportInfo} />
        <div className="flex justify-between">
          {R.isEmpty(flatSubscriber) ? null : (
            <DisplaySubscriber
              data={flatSubscriber}
              onClick={() => setIsOpenReport(true)}
            />
          )}
        </div>
      </div>
      <SubscriberListWithTabs
        data={stackedReportInfo}
        title={formatMessage({
          id: 'notificationTimeline.card.metric.userReaction.inappropriateBreakdown.title',
        })}
        onClose={() => setIsOpenReport(false)}
        className={classes.root}
        isOpen={isOpenReport}
      />
    </>
  )
}

const object = {
  id: PropTypes.number,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  profilePicture: PropTypes.string,
  avatarCharacters: PropTypes.string,
  lastSeen: PropTypes.string,
}
UserReaction.propTypes = {
  data: PropTypes.shape({
    likes: PropTypes.shape({
      liked: PropTypes.arrayOf(PropTypes.shape(object)),
      didntLike: PropTypes.arrayOf(PropTypes.shape(object)),
    }),
    reports: PropTypes.shape({
      SEXUAL_CONTENT: PropTypes.arrayOf(PropTypes.shape(object)),
      VIOLENT_CONTENT: PropTypes.arrayOf(PropTypes.shape(object)),
      HATEFUL_CONTENT: PropTypes.arrayOf(PropTypes.shape(object)),
      HAMEFUL_CONTENT: PropTypes.arrayOf(PropTypes.shape(object)),
      SPAM_CONTENT: PropTypes.arrayOf(PropTypes.shape(object)),
    }),
  }).isRequired,
}

export default UserReaction
